import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronRight, User, ExternalLink } from 'lucide-react';
import { TicketStatusBadge, type TicketStatus } from './TicketStatusBadge';
import { TicketPriorityBadge, type TicketPriority } from './TicketPriorityBadge';
import { UserIdentity } from './UserIdentity';
import { timeAgo } from '../utils/timeAgo';
import { ARCHE_PATH } from '../constants';
import api from '@/lib/api';
import type { SupportUserRef } from '@/lib/api';

interface CustomerContextSidebarProps {
  userId: string;
  /** Enriched identity from the ticket DTO; falls back to userId when absent. */
  user?: SupportUserRef | null;
  currentTicketId: string;
}

interface CustomerTicket {
  ticketId: string;
  subject: string;
  status: string;
  priority: string;
  createdAt: string;
}

export function CustomerContextSidebar({
  userId,
  user,
  currentTicketId,
}: CustomerContextSidebarProps) {
  const [tickets, setTickets] = useState<CustomerTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .listSupportTicketsByUser(userId, { limit: 20 })
      .then((result) => {
        if (!cancelled) {
          setTickets(result.tickets.filter((t) => t.ticketId !== currentTicketId));
        }
      })
      .catch(() => {
        // Non-critical: sidebar still renders without data
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, currentTicketId]);

  const totalTickets = tickets.length + 1; // +1 for current ticket
  const openTickets = tickets.filter(
    (t) => t.status === 'open' || t.status === 'in_progress',
  ).length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center justify-between w-full text-left"
        >
          <CardTitle className="text-sm flex items-center gap-2">
            <User className="h-4 w-4" />
            Customer Context
          </CardTitle>
          <ChevronRight
            className={`h-4 w-4 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        </button>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4">
          {/* Customer stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md bg-muted/50 p-2 text-center">
              <div className="text-lg font-semibold">{loading ? '-' : totalTickets}</div>
              <div className="text-xs text-muted-foreground">Total Tickets</div>
            </div>
            <div className="rounded-md bg-muted/50 p-2 text-center">
              <div className="text-lg font-semibold">{loading ? '-' : openTickets}</div>
              <div className="text-xs text-muted-foreground">Open</div>
            </div>
          </div>

          <div className="rounded-md bg-muted/50 p-2">
            <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Customer
            </div>
            <UserIdentity user={user} userId={userId} className="mt-0.5" />
          </div>

          {/* Previous tickets */}
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : tickets.length === 0 ? (
            <p className="text-xs text-muted-foreground">No other tickets from this customer.</p>
          ) : (
            <div className="space-y-1">
              <h4 className="text-xs font-medium text-muted-foreground">Previous Tickets</h4>
              <div className="max-h-[300px] overflow-y-auto space-y-1.5">
                {tickets.map((t) => (
                  <a
                    key={t.ticketId}
                    href={`${ARCHE_PATH}/admin/tickets/${t.ticketId}`}
                    className="flex items-start gap-2 rounded-md p-2 text-xs hover:bg-muted/50 transition-colors group"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium line-clamp-1 group-hover:text-primary">
                        {t.subject}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <TicketStatusBadge
                          status={t.status as TicketStatus}
                          className="text-[10px] px-1 py-0"
                        />
                        <TicketPriorityBadge
                          priority={t.priority as TicketPriority}
                          className="text-[10px] px-1 py-0"
                        />
                        <span className="text-muted-foreground">{timeAgo(t.createdAt)}</span>
                      </div>
                    </div>
                    <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 mt-0.5 flex-shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
